import { useRef } from 'react'
import type { KeyboardEvent } from 'react'
import type { WizardItemType } from '../../../Model/OrderWizard/OrderWizardDraft.ts'

type TypeOption = { type: WizardItemType; label: string; short: string; name: string; active: string }

const TYPE_OPTIONS: readonly TypeOption[] = [
  { type: 'SAMPLE', label: 'Sample', short: 'S', name: 'Sample', active: 'bg-slate-200 text-slate-800' },
  { type: 'NEGATIVE_CONTROL', label: 'NC', short: 'NC', name: 'Negative control', active: 'bg-sky-100 text-sky-800' }
]

const TYPE_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'])

/** "Sample | NC" on an item's row: a radio group, one tab stop, arrow keys switching. */
export const ItemTypeControl = ({
  name,
  control,
  autoControl,
  compact,
  testId,
  onSetType
}: {
  name: string
  control: boolean
  autoControl: boolean
  compact: boolean
  testId: string
  onSetType: (itemType: WizardItemType) => void
}) => {
  const radios = useRef<(HTMLButtonElement | null)[]>([])
  const current = control ? 1 : 0
  const keyDown = (event: KeyboardEvent) => {
    if (!TYPE_KEYS.has(event.key)) {
      return
    }
    event.preventDefault()
    const next = 1 - current
    onSetType(TYPE_OPTIONS[next].type)
    radios.current[next]?.focus()
  }
  return (
    <span
      role="radiogroup"
      aria-label={`Item type: ${name}`}
      data-testid={testId}
      className="inline-flex shrink-0 overflow-hidden rounded ring-1 ring-inset ring-slate-200"
      onKeyDown={keyDown}
    >
      {TYPE_OPTIONS.map((option, index) => {
        const checked = index === current
        return (
          <button
            key={option.type}
            ref={(element) => {
              radios.current[index] = element
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={option.name}
            title={checked && autoControl && control ? 'Negative control, recognised from the name' : option.name}
            tabIndex={checked ? 0 : -1}
            data-type={option.type}
            className={`px-1.5 text-[10px] font-medium leading-4 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary ${
              checked ? option.active : 'cursor-pointer text-slate-500 hover:text-slate-800'
            }`}
            onClick={() => {
              if (!checked) {
                onSetType(option.type)
              }
            }}
          >
            {compact ? option.short : option.label}
          </button>
        )
      })}
    </span>
  )
}
